import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../theme-context';

interface Props {
  label?: string;
  labelStyle?: string;
  containerStyle?: string;
  value: Date | null;
  onChange: (month: Date | null) => void;
  fromYear?: number;
  toYear?: number;
}

const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const formatMMMYYYY = (d: Date) => `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
const clampYear = (y: number, from?: number, to?: number) => Math.min(to ?? y, Math.max(from ?? y, y));

const DateMonth = ({
  label,
  labelStyle,
  containerStyle,
  value,
  onChange,
  fromYear = 2000,
  toYear = 2100,
}: Props) => {
  const { theme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const initialYear = clampYear(value ? value?.getFullYear() : new Date().getFullYear(), fromYear, toYear);
  const [year, setYear] = useState<number>(initialYear);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (value) {
      const y = clampYear(value?.getFullYear(), fromYear, toYear);
      setYear(y);
    }
  }, [value, fromYear, toYear]);

  const handlePick = (y: number, m: number) => {
    const d = new Date(y, m, 1);
    onChange(d);
    setOpen(false);
  };

  const displayText = value ? formatMMMYYYY(value) : 'MMM YYYY';
  const canPrev = year > fromYear;
  const canNext = year < toYear;

  return (
    <div className={`relative w-full ${containerStyle ?? ''}`} ref={ref}>
      {label && <label className={`block mb-1 font-medium dark:text-white ${labelStyle ?? ''}`}>{label}</label>}
      <div
        onClick={() => setOpen(!open)}
        className='w-full flex items-center justify-between px-3 py-2 border dark:border-[#374151] rounded bg-white dark:bg-black dark:text-white cursor-pointer'
      >
        <span className={`${value ? '' : 'text-primary-light'}`}>{displayText}</span>
        <img
          src={theme === 'dark'
            ? '/assets/insights-tab/calendar-white.svg'
            : '/assets/insights-tab/calendar.svg'}
          className='object-contain w-[24px]'
        />
      </div>
      {open && (
        <div className='absolute mt-1 z-50 p-4 rounded-md shadow-lg bg-white dark:bg-black dark:text-white border dark:border-gray-700 w-[320px]'>
          <div className='flex items-center justify-between mb-3'>
            <button
              disabled={!canPrev}
              onClick={(e) => { e.stopPropagation(); if (canPrev) setYear(y => y - 1); }}
              className={`px-2 py-1 rounded border dark:border-gray-700 ${canPrev ? 'hover:bg-gray-100 dark:hover:bg-gray-800' : 'opacity-40 cursor-not-allowed'}`}
            >
              ‹
            </button>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className='px-2 py-1 border rounded dark:bg-black dark:text-white dark:border-gray-700'
            >
              {Array.from({ length: toYear - fromYear + 1 }, (_, i) => fromYear + i).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <button
              disabled={!canNext}
              onClick={(e) => { e.stopPropagation(); if (canNext) setYear(y => y + 1); }}
              className={`px-2 py-1 rounded border dark:border-gray-700 ${canNext ? 'hover:bg-gray-100 dark:hover:bg-gray-800' : 'opacity-40 cursor-not-allowed'}`}
            >
              ›
            </button>
          </div>
          <div className='grid grid-cols-3 gap-2'>
            {monthNames.map((name, idx) => {
              const isSelected = value && value?.getFullYear() === year && value.getMonth() === idx;
              return (
                <button
                  key={name}
                  onClick={() => handlePick(year, idx)}
                  className={[
                    'px-3 py-2 rounded border text-sm',
                    'dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800',
                    isSelected ? 'bg-blue-500 text-white dark:bg-blue-400 dark:text-black border-transparent' : ''
                  ].join(' ')}
                >
                  {name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DateMonth;
